import * as React from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { Box, Drawer, Snackbar, Alert, Button, Typography } from '@mui/material'
import { show, codeTree } from '../../redux/slices/Drawer/drawerRight'
import { updateFeatures } from '../../redux/slices/Tree/treeFeatures'
import { setShowAccessAlert } from '../../redux/slices/Map/map'
import { getPropertiesByID } from '../../services'
import { detectCityFromURL } from '../../utils/cityDetector'
import { buildCantonUrl } from '../../utils/cantonUrl'
import {
  isDetailNotAllowedError,
  isFullTreeDetail,
  isInvalidSessionError,
  TREE_DETAIL_TYPE,
} from '../../utils/treePermissions'
import { useCities } from '../../context/CitiesContext'
import DrawerHeader from './DrawerHeader'
import DrawerSkeleton from './DrawerSkeleton'
import Accordion from './Accordion'
import TreePhotoGallery from '../TreePhotos/TreePhotoGallery'
import _ from 'lodash'

function getIndexOfFeature(codeId, data) {
  let idFeatureState = null
  data?.forEach((obj, key) => {
    if (obj.properties?.code === codeId) idFeatureState = key
  })
  return idFeatureState
}

// Errores de la ficha completa que se muestran dentro del drawer
const DETAIL_ERROR = {
  NOT_FOUND: { message: 'No se encontró la ficha de este árbol.', canRetry: false },
  LOAD_FAILED: { message: 'No se pudo cargar la ficha completa del árbol.', canRetry: true },
  SESSION_EXPIRED: { message: 'Tu sesión expiró. Vuelve a iniciar sesión.', canRetry: false },
}
const RETRY_LABEL = 'Reintentar'

const DrawerRightInfo = ({ disableNavigationOnClose = false }) => {
  let navigate = useNavigate()
  const dispatch = useDispatch()
  const { treeId: code } = useParams()
  const location = useLocation()
  const city = detectCityFromURL(location.pathname)
  const { getEnabledCantones } = useCities()
  const cantones = getEnabledCantones()
  const [drawerLoading, setDrawerLoading] = React.useState(false)
  const [codeNotFound, setCodeNotFound] = React.useState(false)
  const [detailError, setDetailError] = React.useState(null)
  const [retryNonce, setRetryNonce] = React.useState(0)
  const drawerRequestIdRef = React.useRef(0)

  const { value: open, index: idTreeInside } = useSelector(state => state.drawerRight)
  const stateDrawer = useSelector(state => state.drawerLeft)
  const { features } = useSelector(state => state.treeFeatures?.data)
  const { open: openPopup } = useSelector(state => state.mapState.popup)
  const treeFeature = features?.[idTreeInside]
  const authenticated = useSelector(state => state.login.authenticated)
  const isAuthenticated = !!authenticated?.auth
  const treeId = treeFeature?.properties?.id
  const treeCode = treeFeature?.properties?.code || code
  const hasFullDetail = isFullTreeDetail(treeFeature?.properties)
  // Solo se muestra el error del árbol abierto (no el de uno anterior mientras corre el efecto)
  const visibleDetailError = detailError?.treeId === treeId ? detailError : null

  const handleCloseDrawerRight = () => {
    dispatch(show(false))
    dispatch(codeTree(null))
    if (!disableNavigationOnClose && !openPopup) navigate(buildCantonUrl(city, cantones))
  }

  // Effect A: apertura por URL directa (sin popup abierto previamente)
  React.useEffect(() => {
    if (!_.startsWith(location.pathname, `/${city}`)) return
    if (open) return
    if (idTreeInside != null) return
    if (!code) return
    if (!features.length) return

    const insideId = getIndexOfFeature(code, features)
    if (insideId == null) {
      setCodeNotFound(true)
      return
    }

    // Abrir el Drawer inmediatamente para que el skeleton sea visible durante el fetch
    dispatch(show(true))

    const requestId = ++drawerRequestIdRef.current
    setDrawerLoading(true)
    getPropertiesByID(features[insideId].properties.id)
      .then(properties => {
        if (requestId !== drawerRequestIdRef.current) return
        dispatch(updateFeatures({ someId: insideId, someValue: properties }))
        dispatch(codeTree(insideId))
      })
      .catch(err => console.error('Error al obtener propiedades por ID:', err.message))
      .finally(() => {
        if (requestId !== drawerRequestIdRef.current) return
        setDrawerLoading(false)
      })

    return () => { drawerRequestIdRef.current += 1 }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, features])

  // Effect B: apertura via "Ver más" — obtiene datos completos si solo hay datos de popup.
  // Solo se fusiona en Redux la ficha 'full': sin acceso se cierra el drawer y se avisa, sin
  // cambiar treeFeatures, así que ni este efecto ni el del popup repiten la petición.
  React.useEffect(() => {
    if (!open || idTreeInside == null || !treeId) return
    const currentDataType = treeFeature?.properties?._dataType
    if (currentDataType === TREE_DETAIL_TYPE.FULL) {
      setDrawerLoading(false)
      return
    }

    const closeWithAccessAlert = () => {
      setDrawerLoading(false)
      dispatch(show(false))
      dispatch(codeTree(null))
      dispatch(setShowAccessAlert(true))
    }

    const requestId = ++drawerRequestIdRef.current
    setDrawerLoading(true)
    getPropertiesByID(treeId)
      .then(properties => {
        if (requestId !== drawerRequestIdRef.current) return
        if (!properties) {
          setDetailError({ treeId, ...DETAIL_ERROR.NOT_FOUND })
          return
        }
        if (isFullTreeDetail(properties)) {
          dispatch(updateFeatures({ someId: idTreeInside, someValue: properties }))
          return
        }
        // Proyección restringida (_accessRestricted) o cualquier respuesta que no sea 'full'
        closeWithAccessAlert()
      })
      .catch(error => {
        if (requestId !== drawerRequestIdRef.current) return
        if (isDetailNotAllowedError(error)) {
          closeWithAccessAlert()
          return
        }
        const detailError = isInvalidSessionError(error) ? DETAIL_ERROR.SESSION_EXPIRED : DETAIL_ERROR.LOAD_FAILED
        setDetailError({ treeId, ...detailError })
      })
      .finally(() => {
        if (requestId !== drawerRequestIdRef.current) return
        setDrawerLoading(false)
      })

    // El error es de esta petición: se descarta al cerrar, cambiar de árbol o reintentar
    return () => {
      drawerRequestIdRef.current += 1
      setDetailError(null)
    }
  }, [open, idTreeInside, treeFeature?.properties?._dataType, treeId, dispatch, retryNonce])

  if (!isAuthenticated) return null
  if (!open && !Boolean(treeFeature)) return null

  return (
    <Box>
      <Drawer
        anchor="right"
        open={open}
        PaperProps={{
          sx: {
            top: stateDrawer.drawerHeight,
            height: stateDrawer.contentBox,
            minWidth: '334px',
            width: '25vw',
            backgroundColor: '#E7EBF0',
          },
        }}
        elevation={16}
        variant="persistent">
        <DrawerHeader
          scientificName={treeFeature?.properties?.specie?.name}
          commonName={treeFeature?.properties?.specie?.pseudonym?.[0]?.name}
          classifier={treeFeature?.properties?.specie?.classifier}
          onClick={handleCloseDrawerRight}
        />
        {visibleDetailError ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
              {visibleDetailError.message}
            </Typography>
            {visibleDetailError.canRetry && (
              <Button size="small" variant="outlined" onClick={() => setRetryNonce(n => n + 1)}>
                {RETRY_LABEL}
              </Button>
            )}
          </Box>
        ) : drawerLoading || !hasFullDetail ? (
          <DrawerSkeleton />
        ) : (
          <>
            {(treeId || treeCode) && (
              <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                <TreePhotoGallery key={treeId || treeCode} treeId={treeId} treeCode={treeCode} />
              </Box>
            )}
            <Accordion data={treeFeature} />
          </>
        )}
      </Drawer>

      <Snackbar
        open={codeNotFound}
        autoHideDuration={5000}
        onClose={() => setCodeNotFound(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert
          severity="warning"
          onClose={() => setCodeNotFound(false)}
          sx={{ width: '100%' }}>
          No se encontró ningún árbol con el código <strong>{code}</strong>
        </Alert>
      </Snackbar>
    </Box>
  )
}
export default DrawerRightInfo
